import { useState } from 'react';

const NONE: ReadonlySet<string> = new Set();

// Beyond this many rows arriving at once it reads as a bulk load, not live
// events, so the cascade is skipped.
export const MAX_CASCADE_ROWS = 5;

// The run of brand-new keys at the head of the list, but only when it sits on
// top of the list we were already showing. A list that turned over completely
// was replaced rather than extended, and replacements never animate.
export function getArrivedRowKeys(
  previousKeys: readonly string[],
  nextKeys: readonly string[],
): string[] {
  const previous = new Set(previousKeys);
  let leading = 0;
  while (leading < nextKeys.length && !previous.has(nextKeys[leading])) {
    leading += 1;
  }
  if (leading === 0 || !previous.has(nextKeys[leading])) return [];
  return nextKeys.slice(0, leading);
}

interface Snapshot {
  keys: string[];
  listId: unknown;
  entering: ReadonlySet<string>;
}

// Which of ``keys`` just arrived at the head of the list, ready before the new
// rows run their mount effect. ``listId`` identifies *which* list is on screen
// — change it (a different node, a different filter) and the next keys are
// treated as a replacement, so nothing animates.
export function useEnteringRows(
  keys: string[],
  listId?: unknown,
): ReadonlySet<string> {
  // Derived state rather than a ref: a concurrent render that never commits
  // must not leave its keys behind as the baseline for the one that does.
  const [snapshot, setSnapshot] = useState<Snapshot>({
    keys,
    listId,
    entering: NONE,
  });

  if (snapshot.keys !== keys || !Object.is(snapshot.listId, listId)) {
    const replaced = !Object.is(snapshot.listId, listId);
    const arrived = replaced ? [] : getArrivedRowKeys(snapshot.keys, keys);
    setSnapshot({
      keys,
      listId,
      entering:
        arrived.length > 0 && arrived.length <= MAX_CASCADE_ROWS
          ? new Set(arrived)
          : NONE,
    });
  }

  return snapshot.entering;
}
